import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Inline, Spinner, IconButton } from '@airbus/components-react';
import CloseIcon from '@mui/icons-material/Close';
import { LanguageContext } from '../context/LanguageContext';
import Tabs from './Tabs';
import styles from './RightPanelForm.module.css';
import TabMenu from './TabMenu';
import {
  FieldText,
  FieldTextarea,
  FieldNumber,
  FieldDate,
  FieldBool,
  FieldSelect,
} from './formFields';
import { useColumnContextAPI } from '../context/GetAllColumnContext';
import FieldCreatableSelect from './formFields/FieldCreatableSelect';
import useBreakpoint from './useBreakpoint';
import { MOBILE_MEDIA_QUERY } from './breakpoints';
import { hasDisabledSelectedOptionWarning } from '../utils';

export default function RightPanel(props) {
  const { setisformpanelopen, setAlertbar, rowValues, setrows, rows, disableFormClose } = props;
  const {
    allAccessibleColumns,
    allColumns,
    tableDetails,
    accessDetails: access,
    customConfig,
    allDropdowns,
    setAllDropdowns,
  } = useColumnContextAPI();

  const isMobile = useBreakpoint(MOBILE_MEDIA_QUERY);
  const { singleDatasetGroup } = access;
  const canUserCancel = access?.panelCancelBtn ?? false;
  const isFormCanClose = disableFormClose ? false : canUserCancel;
  const { language } = useContext(LanguageContext);
  const [panelLoader, setPanelLoader] = useState(true);
  const [initialValues, setInitialValues] = useState(null);
  const [rowId, setRowId] = useState(null);
  const isDeletionRequestedRow = !!customConfig?.SetIsDeletionRequestedRow?.({ row: rowValues });
  const disabled = !!(rowValues?.status === 'D' || !access?.edit_row || isDeletionRequestedRow);

  const { fieldsList, fieldsGroupList } = useMemo(
    () =>
      fieldConfiguration({
        allAccessibleColumns,
        tableDetails,
        language,
        disabled,
        rowValues,
        allDropdowns,
      }),
    [allAccessibleColumns, disabled],
  );

  useEffect(() => {
    setPanelLoader(false);
    if (rowValues) {
      if (tableDetails?.mbdDataGrid.formDataSource && customConfig?.SetFormValue) {
        const { SetFormValue } = customConfig;
        setPanelLoader(true);
        const filteredColumns = allColumns.filter((item) => !item.attrAvailableInGrid);
        SetFormValue({
          filteredColumns,
          rowValues,
          setInitialValues,
          setPanelLoader,
          setAlertbar,
          tableDetails,
        });
        return;
      }
      const row = rows.find((item) => item.id == rowValues.id);
      setRowId(row?.id);
      setInitialValues({ ...row });
    }
  }, [rowValues, rows]);

  const handleEnterTabBlur = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      const target = e.target as HTMLInputElement;
      target.blur();
    }
  };

  const blurHandler = (name, value) => {
    setrows((prestate) => {
      const newState = [...prestate];
      const index = newState.findIndex((item) => item.id == rowId);
      const row = newState.find((item) => item.id === rowId);
      if (row[name] === value) {
        return prestate;
      }
      row[name] = value ?? null;
      if (row.status === 'N' || row.status === 'C') {
        row.touched[name] = true;
      }
      row.status = row.status || 'E';
      newState.splice(index, 1, row);
      return newState;
    });
  };

  return (
    <div className={styles.panelWrapper}>
      <div className={styles.iconAlign}>
        {panelLoader && (
          <IconButton variant='ghost'>
            <Spinner />
          </IconButton>
        )}

        {isFormCanClose && singleDatasetGroup && fieldsGroupList.length > 1 && !isMobile ? (
          <TabMenu
            tabMenu={GroupFieldsConfig({
              fieldsList: fieldsGroupList,
              initialValues,
              rowId,
            })}
          />
        ) : null}

        {!panelLoader && isFormCanClose && !isMobile && (
          <IconButton
            variant='ghost'
            aria-label='close'
            onClick={() => {
              setisformpanelopen(false);
            }}
            className={`${styles.closeButton} ${
              fieldsGroupList.length > 1 ? styles.withTabs : styles.noTabs
            }`}
          >
            <CloseIcon />
          </IconButton>
        )}
      </div>

      <div
        className={`${styles.rightPanel} h-100`}
        data-testid={`${tableDetails?.mbdDataGrid.dataGridSource}_rightpanelBox`}
      >
        {initialValues && !!Object.keys(initialValues).length && (
          <form
            onSubmit={(e) => e.preventDefault()}
            noValidate
            autoComplete='off'
            className='right-form h-100'
          >
            {(() => {
              if (singleDatasetGroup) {
                let groupContent;
                if (fieldsGroupList.length > 1) {
                  groupContent = (
                    <Tabs
                      tabs={GroupFieldsConfig({
                        fieldsList: fieldsGroupList,
                        initialValues,
                        rowId,
                        allDropdowns,
                        setAllDropdowns,
                        blurHandler,
                        handleEnterTabBlur,
                      })}
                    />
                  );
                } else {
                  groupContent = (
                    <div className={styles.noGroupScrollContainer}>
                      <div className={styles.noGroupScrollInner}>
                        {GroupFieldsConfig({
                          fieldsList: fieldsGroupList,
                          initialValues,
                          rowId,
                          allDropdowns,
                          setAllDropdowns,
                          blurHandler,
                          handleEnterTabBlur,
                        })[0]?.Component ?? null}
                      </div>
                    </div>
                  );
                }
                return groupContent;
              } else {
                return (
                  <Inline
                    wrap
                    padding='1-x'
                    align='center'
                    className={styles.fieldContainer}
                    childrenClassName={styles.fieldBox}
                  >
                    {fieldsList.map((fields) => (
                      <FormikMuiField
                        key={fields.name}
                        {...{
                          ...fields,
                          initialValues,
                          rowId,
                          allDropdowns,
                          setAllDropdowns,
                          blurHandler,
                          handleEnterTabBlur,
                        }}
                      />
                    ))}
                  </Inline>
                );
              }
            })()}
          </form>
        )}
      </div>
    </div>
  );
}

function GroupFieldsConfig(props) {
  const {
    fieldsList,
    initialValues,
    rowId,
    allDropdowns,
    setAllDropdowns,
    blurHandler,
    handleEnterTabBlur,
  } = props;
  const tabsList = fieldsList.reduce((tabArr, obj) => {
    const { label, list } = obj;
    const fieldsComp = list.map((fields) => (
      <FormikMuiField
        key={fields.name}
        {...{
          ...fields,
          initialValues,
          rowId,
          allDropdowns,
          setAllDropdowns,
          blurHandler,
          handleEnterTabBlur,
        }}
      />
    ));
    tabArr.push({
      label,
      Component: (
        <Inline
          wrap
          padding='1-x'
          alignY='start'
          className={styles.fieldContainer}
          childrenClassName={styles.fieldBox}
        >
          {fieldsComp}
        </Inline>
      ),
    });
    return tabArr;
  }, []);

  return tabsList;
}
export const fieldConfiguration = (props) => {
  const { allAccessibleColumns, tableDetails, language, disabled, allDropdowns } = props;
  const columns = allAccessibleColumns.reduce((resp, item) => {
    if (
      (tableDetails?.mbdDataGrid.formDataSource && item.attrAvailableInGrid) ||
      !item.formOrderNo ||
      item.type === 'currency'
    ) {
      return resp;
    }
    resp.push(item);
    return resp;
  }, []);

  columns.sort(sortingColumns());
  const list = columns.reduce(
    (acc, obj) => {
      if (
        obj.attrInternalName === tableDetails?.mbdDataGrid?.dataGridUniqueIdentifier &&
        obj.attrInternalName !== 'id1'
      ) {
        return acc;
      }
      const gname = obj.mbdAttributeGroup?.[`attributeGroupName${language}`] ?? 'No Group';
      const {
        attrDataType: type,
        attrInternalName: name,
        attrMandatory: required,
        editable,
        label,
      } = obj;
      const isDisabled = disabled || !editable;
      const fieldObj = {
        id: obj?.id,
        language,
        type,
        name,
        required,
        label,
        disabled: isDisabled,
        field: obj,
        options: [],
      };
      if (type === 'singleSelect') {
        fieldObj.options = allDropdowns[name];
      }
      acc.fieldsList.push(fieldObj);

      const index = acc.fieldsGroupList.findIndex((item) => item.label === gname);
      if (index === -1) {
        acc.fieldsGroupList.push({
          label: gname,
          list: [fieldObj],
        });
      } else {
        acc.fieldsGroupList[index]['list'].push(fieldObj);
      }
      return acc;
    },
    { fieldsList: [], fieldsGroupList: [] },
  );
  return list;
};
interface FormikMuiFieldProps {
  field: string;
  type: string;
  name: string;
  label: string | React.ReactNode;
  required: boolean;
  disabled: boolean;
  defaultValue;
  options;
  language: string;
  initialValues;
  rowId;
  allDropdowns;
  setAllDropdowns;
}
function getResolvedFieldProps(fieldConfig, customConfig, access, initialValues) {
  let { disabled, required, label } = fieldConfig;
  const { allDropdowns } = fieldConfig;

  const { type, name, field, language, options } = fieldConfig;
  const params = {
    field: field.field,
    row: initialValues,
    access,
    allAccessibleColumns: field.allAccessibleColumns,
    language,
    required,
    allDropdowns,
  };

  const filteredOptions =
    type === 'singleSelect' && typeof customConfig?.SetDropdownOptions === 'function'
      ? customConfig.SetDropdownOptions({
          field: name,
          options: options ?? [],
        })
      : options;
  const resolvedOptions = Array.isArray(filteredOptions) ? filteredOptions : options;

  if (customConfig?.SetIsCellEditable) {
    const { SetIsCellEditable } = customConfig;
    const isEditable = SetIsCellEditable(params);
    disabled = disabled || !isEditable;
  }

  if (customConfig?.SetISCellRequired) {
    const { SetISCellRequired } = customConfig;
    required = SetISCellRequired(params);
  }

  if (required && !disabled) {
    label = (
      <>
        {label} <span className={styles.requiredAsterisk}>*</span>
      </>
    );
  }

  return {
    disabled,
    required,
    label,
    options: resolvedOptions,
  };
}

function getCustomFieldComponent(customConfig, name, disabled, props, updatedProps) {
  if (!customConfig?.renderFormField || disabled) {
    return null;
  }

  const renderFormField = customConfig.renderFormField;

  const match = Array.isArray(renderFormField)
    ? renderFormField.find((item) => item?.attrInternalName === name)
    : renderFormField?.attrInternalName === name
      ? renderFormField
      : null;

  if (!match?.component) {
    return null;
  }

  const comp = match.component;
  return typeof comp === 'function' ? comp({ ...props, ...updatedProps }) : comp;
}

function getDefaultFieldComponent(type, field, props, updatedProps) {
  const componentProps = { ...props, ...updatedProps };

  switch (type) {
    case 'singleSelect':
      return field.creatable ? (
        <FieldCreatableSelect {...componentProps} />
      ) : (
        <FieldSelect {...componentProps} />
      );

    case 'boolean':
      return <FieldBool {...componentProps} />;

    case 'date':
      return <FieldDate {...componentProps} />;

    case 'number':
      return <FieldNumber {...componentProps} />;

    case 'textarea':
      return <FieldTextarea {...componentProps} />;

    default:
      return <FieldText {...componentProps} />;
  }
}

function FormikMuiField(props: Readonly<FormikMuiFieldProps>) {
  const { type, name, field, initialValues } = props;

  const { customConfig, accessDetails: access } = useColumnContextAPI();

  const updatedProps = getResolvedFieldProps(props, customConfig, access, initialValues);
  const showDisabledSelectedWarning = hasDisabledSelectedOptionWarning({
    type,
    name,
    initialValues,
    options: updatedProps.options,
    customConfig,
  });

  const customField = getCustomFieldComponent(
    customConfig,
    name,
    updatedProps.disabled,
    props,
    updatedProps,
  );

  if (customField) {
    return <div key={name}>{customField}</div>;
  }

  const fieldComp = getDefaultFieldComponent(type, field, props, {
    ...updatedProps,
    showDisabledSelectedWarning,
  });

  return <div key={name}>{fieldComp}</div>;
}

export function sortingColumns(ascending = true) {
  return function (a, b) {
    if (a.formOrderNo === b.formOrderNo) return 0;
    if (a.formOrderNo === null) return 1;
    if (b.formOrderNo === null) return -1;
    if (ascending) return a.formOrderNo < b.formOrderNo ? -1 : 1;
    return a.formOrderNo < b.formOrderNo ? 1 : -1;
  };
}
